import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Key,
  Menu,
  X,
  LogOut,
  Moon,
  Sun,
  Settings as SettingsIcon,
  Wallet,
  Building2,
  Coins,
  Link2,
  Wrench,
  ChevronDown,
  FileText,
  TestTube,
  BarChart3,
  ExternalLink,
  Search,
  Globe,
  Activity,
  LifeBuoy,
  MessageCircle,
  Shield,
  AlertTriangle,
  UserCheck,
  Scale,
  Info,
  Users,
  TrendingUp,
  BookOpen,
  Zap,
  Play,
  CreditCard,
  Code,
  HelpCircle,
  Phone,
  Grid,
  Users2,
  Award,
  Factory
} from 'lucide-react'
import { useAuthStore } from '@/lib/store/auth'
import { useThemeStore } from '@/lib/store/theme'
import { WalletSelector } from '@/components/wallet/WalletSelector'
import { getDomainConfig } from '@/utils/domainConfig'

interface NavItem {
  path?: string;
  label: string;
  icon?: any;
  children?: NavItem[];
  isDivider?: boolean;
  isExternal?: boolean;
}

// Check if we're on localhost
const isLocalhost = () => {
  if (typeof window === 'undefined') return true;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
};

// Get domain-specific navigation items
const getDomainNavItems = (): NavItem[] => {
  const config = getDomainConfig();
  
  switch (config.name) {
    case 'QSAVE Project':
      return [
        {
          label: 'Recovery',
          icon: LifeBuoy,
          children: [
            { path: '/automatic-migration', label: 'Adaptive Migration', icon: Shield },
            { path: '/recovery', label: 'How recovery works', icon: LifeBuoy },
            // Vendor-neutral hardening page — implementations are on /implementers.
            { path: '/wallet-hardening', label: 'Wallet Hardening', icon: Wallet },
          ]
        },
        { path: '/qd3-registration', label: 'Quantum Watermarking', icon: Shield },
        { path: '/philanthropic-foundation', label: 'Philanthropic Foundation', icon: Globe },
        {
          label: 'Resources',
          icon: FileText,
          children: [
            // Standards Mark held back from qsave.org pending its own independent treatment.
            // Generic link serves the redacted edition; the full edition is linked from /token only.
            { path: '/whitepapers/qsave-whitepaper-public.pdf', label: 'Whitepaper', icon: FileText, isExternal: true },
            { path: '/implementers', label: 'Wallets & Watermarking', icon: Wallet },
            { path: '/faq', label: 'FAQ', icon: MessageCircle },
            { path: '/about', label: 'About', icon: Info },
            { path: 'https://qkey.co', label: 'QKey Products ↗', icon: Building2, isExternal: true }
          ]
        },
        { path: '/token', label: 'QSAVE Token', icon: Coins }
      ];
      
    case 'Quantum Day Response':
      return [
        { path: '/bitcoin-tools', label: 'Check Address', icon: Search },
        { path: '/qd3-registration', label: 'Protect / Register', icon: Shield },
        { path: '/recovery', label: 'Recovery', icon: FileText },
        {
          label: 'Learn',
          icon: BookOpen,
          children: [
            { path: '/about', label: 'About Q-Day', icon: Info },
            { path: '/faq', label: 'FAQ', icon: MessageCircle },
            { path: 'https://qsave.org', label: 'QSAVE Protocol ↗', icon: Shield, isExternal: true }
          ]
        }
      ];
      
    case 'Post-Quantum Pay':
      return [
        { path: '/pqp', label: 'Overview', icon: Zap },
        { path: '/protection-workflow', label: 'How It Works', icon: Shield },
        { path: '/quantum-keys', label: 'Quantum Keys', icon: Wallet },
        {
          label: 'Support',
          icon: LifeBuoy,
          children: [
            { path: '/faq', label: 'FAQ', icon: HelpCircle },
            { path: '/contact', label: 'Contact', icon: Phone },
            { path: 'https://qkey.co', label: 'QKey ↗', icon: Building2, isExternal: true }
          ]
        }
      ];
      
    case 'Development': {
      // The Development config is also the fallback for any hostname not in
      // DOMAIN_CONFIGS, so it can be selected on a real domain. Everything below
      // is public-safe; the internal Tools menu is gated on localhost separately.
      const devNav: NavItem[] = [
        {
          label: 'QSave',
          icon: Shield,
          children: [
            { path: '/qd3-registration', label: 'QD3 Registry', icon: Shield },
            { path: '/standards-mark', label: 'Standards Mark', icon: Award },
            { path: '/pq-chain', label: 'Backup Chain', icon: Link2 },
            { path: '/philanthropic-foundation', label: 'Foundation', icon: Globe },
            { path: 'https://www.qday.is', label: 'Q-Day Monitor', icon: AlertTriangle, isExternal: true },
          ]
        },
        { path: '/quantum-keys', label: 'Key Generator', icon: Key },
        { path: '/hd-wallet', label: 'Wallet', icon: Wallet },
        { path: '/pq-exchange', label: 'Exchange', icon: Building2 },
        { path: '/uspq', label: 'USPQ', icon: CreditCard },
        { path: '/partners', label: 'Partner', icon: Users2 },
        { path: '/token', label: 'Token', icon: Coins }
      ];

      // Internal tooling never ships on a public host — same rule the QKey nav uses.
      if (isLocalhost()) {
        devNav.push({
          label: 'Tools',
          icon: Wrench,
          children: [
            // ALL test tools available in development
            { path: '/tools', label: 'Blockchain Tools', icon: TestTube },
            { path: '/wallets', label: 'Multi-Wallet Dashboard', icon: Wallet },
            { path: '/lattice-visualization', label: 'Lattice Crypto Demo', icon: Activity },
            { path: '/water-drop', label: 'Water Drop Demo', icon: TestTube },
            
            // Test Report Pages
            { path: '/test-report', label: 'Test Reports', icon: FileText },
            { path: '/tools?tab=test-results', label: 'Test Summary', icon: FileText },
            { path: '/tools?tab=coverage', label: 'Test Coverage', icon: BarChart3 },
            { path: '/output-analyzer', label: 'Output Analyzer Test', icon: TestTube },
            { path: '/address-test-table', label: 'Address Test Table', icon: TestTube },
            { label: 'divider', isDivider: true },
            { path: '/bitcoin-tools', label: 'Bitcoin Developer Tools', icon: Wrench },
            { path: '/transaction', label: 'Transaction Builder', icon: FileText },
            { path: '/invoice-generator', label: 'Invoice Generator', icon: FileText },
            { path: '/brand-guidelines', label: 'Brand Guidelines', icon: FileText },
            { path: '/settings', label: 'Settings', icon: SettingsIcon },
            { label: 'divider', isDivider: true },
            { path: 'https://opreturn.org/', label: 'OP_RETURN Explorer', icon: Search, isExternal: true },
            { path: 'https://www.blockchain.com/explorer', label: 'Blockchain Explorer', icon: Globe, isExternal: true },
            { path: 'https://mempool.space/', label: 'Mempool.space', icon: Activity, isExternal: true },
            { path: 'https://btcscan.org/', label: 'BTCScan.org', icon: Search, isExternal: true },
            { path: 'https://statoshi.info/', label: 'Statoshi.info', icon: BarChart3, isExternal: true },
            { path: 'https://bitinfocharts.com/top-100-richest-bitcoin-addresses.html', label: 'BitInfoCharts - Top 100', icon: BarChart3, isExternal: true },
          ]
        });
      }

      return devNav;
    }

    default:
      // QKey commercial site — QKey products only. QSAVE protocol/token pages
      // live on qsave.org (cross-linked below), not in this nav.
      const baseNav: NavItem[] = [
        { path: '/hd-wallet', label: 'Wallet', icon: Wallet },
        { path: '/quantum-vault-pqp', label: 'Factory', icon: Factory },
        { path: '/partners', label: 'Partners', icon: Users2 },
        {
          label: 'More',
          icon: Info,
          children: [
            // Whitepapers withheld from public release — nav link omitted.
            { path: '/faq', label: 'FAQ', icon: MessageCircle },
            { path: '/about', label: 'About', icon: Info },
            { path: 'https://qsave.org', label: 'QSAVE Protocol ↗', icon: Shield, isExternal: true },
          ]
        },
      ];
      
      // Only add Tools menu if we're on localhost
      if (isLocalhost()) {
        baseNav.push({
          label: 'Tools',
          icon: Wrench,
          children: [
            // Test & Analysis Tools - ALL available on localhost
            { path: '/tools', label: 'Blockchain Tools', icon: TestTube },
            { path: '/wallets', label: 'Multi-Wallet Dashboard', icon: Wallet },
            { path: '/lattice-visualization', label: 'Lattice Crypto Demo', icon: Activity },
            { path: '/water-drop', label: 'Water Drop Demo', icon: TestTube },
            
            // Test Report Pages
            { path: '/test-report', label: 'Test Reports', icon: FileText },
            { path: '/tools?tab=test-results', label: 'Test Summary', icon: FileText },
            { path: '/tools?tab=coverage', label: 'Test Coverage', icon: BarChart3 },
            { path: '/output-analyzer', label: 'Output Analyzer Test', icon: TestTube },
            { path: '/address-test-table', label: 'Address Test Table', icon: TestTube },
            
            // Divider
            { label: 'divider', isDivider: true },
            
            // Internal Tools
            { path: '/bitcoin-tools', label: 'Bitcoin Developer Tools', icon: Wrench },
            { path: '/transaction', label: 'Transaction Builder', icon: FileText },
            { path: '/invoice-generator', label: 'Invoice Generator', icon: FileText },
            { path: '/brand-guidelines', label: 'Brand Guidelines', icon: FileText },
            { path: '/settings', label: 'Settings', icon: SettingsIcon },
            
            // Divider
            { label: 'divider', isDivider: true },
            
            // External Tools
            { path: 'https://opreturn.org/', label: 'OP_RETURN Explorer', icon: Search, isExternal: true },
            { path: 'https://www.blockchain.com/explorer', label: 'Blockchain Explorer', icon: Globe, isExternal: true },
            { path: 'https://mempool.space/', label: 'Mempool.space', icon: Activity, isExternal: true },
            { path: 'https://btcscan.org/', label: 'BTCScan.org', icon: Search, isExternal: true },
            { path: 'https://statoshi.info/', label: 'Statoshi.info', icon: BarChart3, isExternal: true },
            { path: 'https://bitinfocharts.com/top-100-richest-bitcoin-addresses.html', label: 'BitInfoCharts - Top 100', icon: BarChart3, isExternal: true },
          ]
        });
      }
      
      return baseNav;
  }
}

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const location = useLocation()
  const { logout } = useAuthStore()
  const { theme, toggleTheme } = useThemeStore()
  const dropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const domainConfig = getDomainConfig()

  // Get domain-specific branding
  const getDomainBranding = () => {
    switch (domainConfig.name) {
      case 'QSAVE Project':
        return {
          logo: '/images/logos/qsave-logo.png',
          icon: Shield,
          name: 'QSAVE',
          tagline: '',
          color: 'text-green-600',
          bgColor: 'bg-green-600',
          showAlpha: false
        }
      case 'Philanthropic Foundation':
        return {
          logo: null,
          icon: Globe,
          name: 'NWF',
          tagline: 'Philanthropic Foundation',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-600',
          showAlpha: false
        }
      case 'Quantum Day Response':
        return {
          logo: null,
          icon: AlertTriangle,
          name: 'Q-Day',
          tagline: 'Quantum Threat Monitor',
          color: 'text-red-600',
          bgColor: 'bg-red-600',
          showAlpha: false
        }
      case 'Post-Quantum Pay':
        return {
          logo: '/images/logos/pqp-wordmark.svg',
          icon: Zap,
          name: 'PQP',
          tagline: 'Post-Quantum Pay',
          color: 'text-purple-600',
          bgColor: 'bg-purple-600',
          showAlpha: false
        }
      default:
        return {
          logo: '/images/logos/qkey-padlock.png',
          icon: Wallet,
          name: 'QKey',
          tagline: 'Quantum Protection',
          color: 'text-quantum-600',
          bgColor: 'bg-quantum-600',
          showAlpha: false
        }
    }
  }

  const branding = getDomainBranding()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      let clickedInside = false
      dropdownRefs.current.forEach((ref) => {
        if (ref && ref.contains(event.target as Node)) {
          clickedInside = true
        }
      })
      if (!clickedInside) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    logout()
    setIsOpen(false)
  }

  const toggleDropdown = (label: string) => {
    // Clear any pending close timeout when clicking
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setOpenDropdown(openDropdown === label ? null : label)
  }

  const handleMouseEnter = (label: string) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setOpenDropdown(label)
  }

  const handleMouseLeave = () => {
    // Add a 300ms delay before closing to allow mouse movement
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null)
    }, 300)
  }

  const renderNavItem = (item: NavItem, isMobile: boolean = false) => {
    const hasChildren = item.children && item.children.length > 0
    const Icon = item.icon

    // Special handling for QSave tile dropdown
    if (item.label === 'QSave' && hasChildren && !isMobile) {
      return (
        <div 
          key={item.label} 
          className="relative" 
          ref={(el) => {
            if (el) {
              dropdownRefs.current.set(item.label, el)
            } else {
              dropdownRefs.current.delete(item.label)
            }
          }}
          onMouseEnter={() => handleMouseEnter('QSave')}
          onMouseLeave={handleMouseLeave}
        >
          <button
            onClick={() => toggleDropdown(item.label)}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
              text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800
            `}
          >
            <Icon className="w-4 h-4" />
            <span>{item.label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
          </button>
          
          {openDropdown === item.label && (
            <div 
              className="absolute mt-1 pt-1 left-0 w-[600px]"
              onMouseEnter={() => handleMouseEnter('QSave')}
              onMouseLeave={handleMouseLeave}
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="grid grid-cols-2 gap-3">
                {item.children?.map(child => {
                  const ChildIcon = child.icon
                  
                  // Define gradient colors for each item
                  const gradients: Record<string, string> = {
                    'QD3 Registry': 'from-blue-500 to-indigo-600',
                    'Standards Mark': 'from-green-500 to-emerald-600',
                    'Backup Chain': 'from-purple-500 to-pink-600',
                    'Fund': 'from-amber-500 to-orange-600',
                    'Q-Day Monitor': 'from-red-500 to-rose-600'
                  }
                  
                  const gradient = gradients[child.label] || 'from-gray-500 to-gray-600'
                  
                  if (child.isExternal) {
                    return (
                      <a
                        key={child.path}
                        href={child.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpenDropdown(null)}
                        className="group relative overflow-hidden rounded-lg bg-gradient-to-br p-[1px] hover:scale-[1.02] transition-all duration-200"
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-90`} />
                        <div className="relative bg-white dark:bg-gray-800 m-[1px] rounded-lg p-4 group-hover:bg-opacity-95 dark:group-hover:bg-opacity-95 transition-all">
                          <div className="flex items-start space-x-3">
                            {ChildIcon && (
                              <div className={`p-2 rounded-lg bg-gradient-to-br ${gradient} text-white`}>
                                <ChildIcon className="w-5 h-5" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center">
                                <span className="font-medium text-gray-900 dark:text-white">{child.label}</span>
                                <ExternalLink className="w-3 h-3 ml-2 text-gray-400" />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">External resource</span>
                            </div>
                          </div>
                        </div>
                      </a>
                    )
                  }
                  
                  return (
                    <Link
                      key={child.path}
                      to={child.path!}
                      onClick={() => setOpenDropdown(null)}
                      className="group relative overflow-hidden rounded-lg bg-gradient-to-br p-[1px] hover:scale-[1.02] transition-all duration-200"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-90`} />
                      <div className="relative bg-white dark:bg-gray-800 m-[1px] rounded-lg p-4 group-hover:bg-opacity-95 dark:group-hover:bg-opacity-95 transition-all">
                        <div className="flex items-start space-x-3">
                          {ChildIcon && (
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${gradient} text-white`}>
                              <ChildIcon className="w-5 h-5" />
                            </div>
                          )}
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 dark:text-white">{child.label}</span>
                            {child.label === 'QD3 Registry' && <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">Quantum-resistant signatures</span>}
                            {child.label === 'Standards Mark' && <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">Certification levels</span>}
                            {child.label === 'Recovery' && <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">Recover compromised Bitcoin</span>}
                            {child.label === 'Backup Chain' && <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">Post-quantum blockchain</span>}
                            {child.label === 'Fund' && <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">Community philanthropic foundation</span>}
                          </div>
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
              </div>
            </div>
          )}
        </div>
      )
    }
    
    // Special handling for Tools mega dropdown
    if (item.label === 'Tools' && hasChildren && !isMobile) {
      return (
        <div 
          key={item.label} 
          className="relative" 
          ref={(el) => {
            if (el) {
              dropdownRefs.current.set(item.label, el)
            } else {
              dropdownRefs.current.delete(item.label)
            }
          }}
          onMouseEnter={() => handleMouseEnter('Tools')}
          onMouseLeave={handleMouseLeave}
        >
          <button
            onClick={() => toggleDropdown(item.label)}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
              text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800
            `}
          >
            <Icon className="w-4 h-4" />
            <span>{item.label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
          </button>
          
          {openDropdown === item.label && (
            <div 
              className="absolute mt-1 pt-1 -left-96 w-[800px]"
              onMouseEnter={() => handleMouseEnter('Tools')}
              onMouseLeave={handleMouseLeave}
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="grid grid-cols-3 gap-6">
                {/* Testing & Analytics */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Testing & Analytics</h3>
                  <div className="space-y-1">
                    {item.children?.filter(child => {
                      const testTools = ['Test Reports', 'Test Summary', 'Test Coverage', 'Address Test Table', 'Output Analyzer'];
                      const productionTools = ['Blockchain Tools', 'Multi-Wallet Dashboard'];
                      // Children are already filtered where they are built (see getDomainNavItems).
                      return [...testTools, ...productionTools].includes(child.label);
                    }).map(child => (
                      <Link
                        key={child.path}
                        to={child.path!}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {child.icon && <child.icon className="w-4 h-4" />}
                        <span>{child.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Developer Tools */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Developer Tools</h3>
                  <div className="space-y-1">
                    {item.children?.filter(child => {
                      const devTools = ['Water Drop Demo'];
                      const allTools = ['Bitcoin Developer Tools', 'Transaction Builder', 'Invoice Generator', 'Brand Guidelines', 'Settings'];
                      // Children are already filtered where they are built (see getDomainNavItems).
                      return [...allTools, ...devTools].includes(child.label);
                    }).map(child => (
                      <Link
                        key={child.path}
                        to={child.path!}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {child.icon && <child.icon className="w-4 h-4" />}
                        <span>{child.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* External Tools Section */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">External Resources</h3>
                <div className="grid grid-cols-3 gap-4">
                  {item.children?.filter(child => child.isExternal).map(child => (
                    <a
                      key={child.path}
                      href={child.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpenDropdown(null)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {child.icon && <child.icon className="w-4 h-4" />}
                      <span>{child.label}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </a>
                  ))}
                </div>
              </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    if (hasChildren) {
      return (
        <div 
          key={item.label} 
          className="relative" 
          ref={(el) => {
            if (el) {
              dropdownRefs.current.set(item.label, el)
            } else {
              dropdownRefs.current.delete(item.label)
            }
          }}
          onMouseEnter={() => !isMobile && handleMouseEnter(item.label)}
          onMouseLeave={() => !isMobile && handleMouseLeave()}
        >
          <button
            onClick={() => toggleDropdown(item.label)}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
              text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800
            `}
          >
            <Icon className="w-4 h-4" />
            <span>{item.label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
          </button>
          
          {openDropdown === item.label && item.children && (
            <div className={`${isMobile ? 'relative' : 'absolute'} ${isMobile ? 'mt-1' : 'mt-1 pt-1'} ${isMobile ? 'w-full' : 'w-56'}`}>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {item.children.map((child, index) => {
                // Handle divider
                if (child.isDivider) {
                  return (
                    <div key={`divider-${index}`} className="my-1">
                      <hr className="border-gray-200 dark:border-gray-700" />
                    </div>
                  )
                }
                
                const ChildIcon = child.icon
                const isActive = location.pathname === child.path
                
                // Handle external links
                if (child.isExternal) {
                  return (
                    <a
                      key={child.path}
                      href={child.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setOpenDropdown(null)
                        if (isMobile) setIsOpen(false)
                      }}
                      className={`
                        flex items-center space-x-3 px-4 py-3 text-sm transition-colors
                        text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700
                      `}
                    >
                      {ChildIcon && <ChildIcon className="w-4 h-4" />}
                      <span>{child.label}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </a>
                  )
                }
                
                return (
                  <Link
                    key={child.path}
                    to={child.path!}
                    onClick={() => {
                      setOpenDropdown(null)
                      if (isMobile) setIsOpen(false)
                    }}
                    className={`
                      flex items-center space-x-3 px-4 py-3 text-sm transition-colors
                      ${isActive 
                        ? 'bg-quantum-100 text-quantum-700 dark:bg-quantum-900 dark:text-quantum-300' 
                        : 'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    {ChildIcon && <ChildIcon className="w-4 h-4" />}
                    <span>{child.label}</span>
                  </Link>
                )
              })}
              </div>
            </div>
          )}
        </div>
      )
    }

    const isActive = location.pathname === item.path
    
    return (
      <Link
        key={item.path}
        to={item.path!}
        onClick={() => isMobile && setIsOpen(false)}
        className={`
          flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
          ${isActive 
            ? 'bg-quantum-100 text-quantum-700 dark:bg-quantum-900 dark:text-quantum-300' 
            : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
          }
        `}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span>
          {item.label}
          {item.label === 'Exchange' && <sup className="text-red-500 text-xs ml-0.5">Future</sup>}
          {item.label === 'USPQ' && <sup className="text-red-500 text-xs ml-0.5">Future</sup>}
        </span>
      </Link>
    )
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            {branding.logo ? (
              <img
                src={branding.logo}
                alt={branding.name}
                className="h-10 w-auto"
              />
            ) : (
              <div className={`p-2 rounded-lg ${branding.bgColor} text-white`}>
                <branding.icon className="w-6 h-6" />
              </div>
            )}
            <div className="flex flex-col">
              <div className={`text-lg ${branding.name === 'QSAVE' ? 'font-charter font-normal text-[22px]' : 'font-semibold'} text-gray-900 dark:text-white flex items-center`}>
                {branding.name}
                {branding.showAlpha && <sup className="text-xs font-normal text-red-500 ml-0.5">alpha</sup>}
              </div>
              {domainConfig.name !== 'Development' && branding.tagline && (
                <span className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                  {branding.tagline}
                </span>
              )}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {getDomainNavItems()
              .filter((item) => {
                // Filter out items that are not allowed for this domain
                if (!item.path) return true // Keep dropdowns
                
                // Check if the path is blocked for this domain
                const isBlocked = domainConfig.blockedPaths.some(blocked => 
                  item.path?.startsWith(blocked)
                )
                
                // If domain has specific allowed paths, only show those
                if (domainConfig.allowedPaths.length > 0 && item.path) {
                  // Check for wildcard
                  if (domainConfig.allowedPaths.includes('*')) {
                    return !isBlocked;
                  }
                  const isAllowed = domainConfig.allowedPaths.some(allowed => 
                    item.path?.startsWith(allowed)
                  )
                  return isAllowed && !isBlocked
                }
                
                // Otherwise show all except blocked
                return !isBlocked
              })
              .map((item) => {
                // Filter children for dropdowns
                if (item.children) {
                  const filteredChildren = item.children.filter(child => {
                    if (!child.path || child.isDivider || child.isExternal) return true
                    
                    const isBlocked = domainConfig.blockedPaths.some(blocked => 
                      child.path?.startsWith(blocked)
                    )
                    
                    if (domainConfig.allowedPaths.length > 0) {
                      // Check for wildcard
                      if (domainConfig.allowedPaths.includes('*')) {
                        return !isBlocked;
                      }
                      const isAllowed = domainConfig.allowedPaths.some(allowed => 
                        child.path?.startsWith(allowed)
                      )
                      return isAllowed && !isBlocked
                    }
                    
                    return !isBlocked
                  })
                  
                  // Only show dropdown if it has children after filtering
                  if (filteredChildren.length === 0) return null
                  
                  return { ...item, children: filteredChildren }
                }
                return item
              })
              .filter((item): item is NavItem => item !== null)
              .map((item) => renderNavItem(item))}
          </div>

          {/* Theme Toggle & Actions */}
          <div className="flex items-center space-x-2">
            {/* Wallet Selector */}
            <WalletSelector />
            <Link
              to="/settings"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Settings"
            >
              <SettingsIcon className="w-5 h-5" />
            </Link>
            
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Logout is a vestige of the retired password gate — auth is disabled
                site-wide, so it only appears on localhost for development. */}
            {isLocalhost() && (
              <button
                onClick={handleLogout}
                className="hidden lg:flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-all duration-200"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-700">
            <div className="py-2 space-y-1">
              {getDomainNavItems()
                .filter((item) => {
                  // Same filtering logic as desktop
                  if (!item.path) return true
                  
                  const isBlocked = domainConfig.blockedPaths.some(blocked => 
                    item.path?.startsWith(blocked)
                  )
                  
                  if (domainConfig.allowedPaths.length > 0 && item.path) {
                    // Check for wildcard
                    if (domainConfig.allowedPaths.includes('*')) {
                      return !isBlocked;
                    }
                    const isAllowed = domainConfig.allowedPaths.some(allowed => 
                      item.path?.startsWith(allowed)
                    )
                    return isAllowed && !isBlocked
                  }
                  
                  return !isBlocked
                })
                .map((item) => {
                  // Filter children for dropdowns
                  if (item.children) {
                    const filteredChildren = item.children.filter(child => {
                      if (!child.path || child.isDivider || child.isExternal) return true
                      
                      const isBlocked = domainConfig.blockedPaths.some(blocked => 
                        child.path?.startsWith(blocked)
                      )
                      
                      if (domainConfig.allowedPaths.length > 0) {
                        // Check for wildcard
                        if (domainConfig.allowedPaths.includes('*')) {
                          return !isBlocked;
                        }
                        const isAllowed = domainConfig.allowedPaths.some(allowed => 
                          child.path?.startsWith(allowed)
                        )
                        return isAllowed && !isBlocked
                      }
                      
                      return !isBlocked
                    })
                    
                    if (filteredChildren.length === 0) return null
                    
                    return { ...item, children: filteredChildren }
                  }
                  return item
                })
                .filter((item): item is NavItem => item !== null)
                .map((item) => renderNavItem(item, true))}
              
              {isLocalhost() && (
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-all duration-200 w-full text-left"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
